import { useSearchParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowLeft, ChevronDown, Bell, Settings, ShieldCheck, Brain, BarChart3, ClipboardList, ExternalLink } from 'lucide-react';
import MarketChart, { type TvInterval } from '@/components/market/MarketChart';
import { useLiveForex } from '@/contexts/LiveForexContext';
import { UnsupportedInstrumentModal } from '@/components/market/UnsupportedInstrumentModal';
import { supabase } from '@/integrations/supabase/client';
import { useMarketPrices } from '@/hooks/useMarketPrices';
import { parseSymbol, resolveTradingViewSymbol, Instrument, instrumentRegistry } from '@/data/instrumentRegistry';
import InstrumentList from '@/components/trading-zone/InstrumentList';
import BrokerSwitcherPill from '@/components/trading-zone/BrokerSwitcherPill';
import ChartToolbar, { INDICATOR_STUDY_MAP } from '@/components/trading-zone/ChartToolbar';
import OrderPanel from '@/components/trading-zone/OrderPanel';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';

export type TradingState = 'no-demo' | 'demo-only' | 'broker-connected';

function getSpread(instrument: Instrument | null): number {
  if (!instrument) return 0;
  if (instrument.asset_class === 'forex') return 0.0002;
  if (instrument.internal_symbol === 'XAU/USD') return 0.30;
  if (instrument.internal_symbol === 'XAG/USD') return 0.05;
  if (instrument.asset_class === 'crypto') return 1.0;
  if (instrument.asset_class === 'commodity') return 0.03;
  if (instrument.asset_class === 'stock') return 0.02;
  return 0.5;
}

// Mobile tabbed view — full-height Chart or Order panel
const MobileTradingTabs = ({
  tvSymbol, interval, setInterval: setIntervalProp, handleIndicatorToggle,
  activeIndicators, currentInstrument, studies, cleanSymbol, displayPrice,
  tradingState, brokerName, demoAccountId,
}: {
  tvSymbol: string; interval: TvInterval; setInterval: (v: TvInterval) => void;
  handleIndicatorToggle: (id: string) => void; activeIndicators: string[];
  currentInstrument: Instrument | null; studies: string[];
  cleanSymbol: string; displayPrice: number | null;
  tradingState: TradingState; brokerName: string | null; demoAccountId: string | null;
}) => {
  const [tab, setTab] = useState<'chart' | 'order'>('chart');
  return (
    <div className="md:hidden flex flex-col overflow-hidden flex-1">
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
        {tab === 'chart' ? (
          <>
            <ChartToolbar
              interval={interval}
              onIntervalChange={setIntervalProp}
              onIndicatorToggle={handleIndicatorToggle}
              activeIndicators={activeIndicators}
              symbol={currentInstrument?.display_name || ''}
            />
            <div className="flex-1 min-h-0">
              {tvSymbol && (
                <MarketChart symbol={tvSymbol} interval={interval} theme="dark" className="h-full" studies={studies} />
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 overflow-y-auto">
            <OrderPanel
              symbol={cleanSymbol}
              currentPrice={displayPrice ?? 0}
              instrumentDecimals={currentInstrument?.decimals ?? 2}
              tradingState={tradingState}
              brokerName={brokerName}
              demoAccountId={demoAccountId}
            />
          </div>
        )}
      </div>
      <div className="shrink-0 flex border-t border-border bg-card" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <button
          onClick={() => setTab('chart')}
          className={`flex-1 flex flex-col items-center gap-0.5 py-2 transition-colors ${
            tab === 'chart' ? 'text-primary' : 'text-muted-foreground'
          }`}
        >
          <BarChart3 className="w-5 h-5" />
          <span className="text-[10px] font-medium">Chart</span>
        </button>
        <button
          onClick={() => setTab('order')}
          className={`flex-1 flex flex-col items-center gap-0.5 py-2 transition-colors ${
            tab === 'order' ? 'text-primary' : 'text-muted-foreground'
          }`}
        >
          <ClipboardList className="w-5 h-5" />
          <span className="text-[10px] font-medium">Order</span>
        </button>
      </div>
    </div>
  );
};

const TradingZone = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { ticks } = useLiveForex();
  const { prices } = useMarketPrices(5000);

  const symbolParam = searchParams.get('symbol') || 'OANDA:EURUSD';
  const [tvSymbol, setTvSymbol] = useState('');
  const [interval, setInterval] = useState<TvInterval>('D');
  const [currentInstrument, setCurrentInstrument] = useState<Instrument | null>(null);
  const [showUnsupportedModal, setShowUnsupportedModal] = useState(false);
  const [activeIndicators, setActiveIndicators] = useState<string[]>(['RSI', 'MACD']);
  const [instrumentPopoverOpen, setInstrumentPopoverOpen] = useState(false);

  const [marketPrice, setMarketPrice] = useState<number | null>(null);
  const isCommodity = currentInstrument?.asset_class === 'commodity';

  // === STATE MACHINE: Query demo_accounts + broker_connections ===
  const { data: demoAccount, refetch: refetchDemo } = useQuery({
    queryKey: ['trading-zone-demo', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from('demo_accounts')
        .select('id, balance')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();
      return data;
    },
    enabled: !!user,
    staleTime: 30000,
  });

  const { data: brokerConnection } = useQuery({
    queryKey: ['trading-zone-broker', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from('broker_connections')
        .select('id, broker_name, status')
        .eq('user_id', user.id)
        .in('status', ['active', 'connected', 'verified'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return data;
    },
    enabled: !!user,
    staleTime: 30000,
  });

  const tradingState: TradingState = !demoAccount ? 'no-demo' : brokerConnection ? 'broker-connected' : 'demo-only';
  const brokerName = brokerConnection?.broker_name || null;
  const demoAccountId = demoAccount?.id || null;

  useEffect(() => {
    const resolved = resolveTradingViewSymbol(symbolParam);
    const instrument = parseSymbol(symbolParam);
    if (resolved && instrument?.supported) {
      setTvSymbol(resolved);
      setCurrentInstrument(instrument);
    } else {
      setShowUnsupportedModal(true);
    }
  }, [symbolParam]);

  useEffect(() => {
    if (!isCommodity || !currentInstrument) { setMarketPrice(null); return; }
    const fetch_ = async () => {
      const { data } = await supabase
        .from('market_prices')
        .select('price')
        .eq('symbol', currentInstrument.internal_symbol)
        .maybeSingle();
      if (data) setMarketPrice(data.price);
    };
    fetch_();
    const id = window.setInterval(fetch_, 30000);
    return () => clearInterval(id);
  }, [isCommodity, currentInstrument?.internal_symbol]);

  // AI Signal for current symbol
  const internalSymbol = currentInstrument?.internal_symbol || '';
  const { data: aiSignal } = useQuery({
    queryKey: ['ai-signal-banner', internalSymbol],
    queryFn: async () => {
      if (!internalSymbol) return null;
      const { data } = await supabase
        .from('ai_signals')
        .select('side, confidence, symbol')
        .eq('symbol', internalSymbol)
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return data;
    },
    enabled: !!internalSymbol,
    staleTime: 30000,
  });

  const handleSymbolChange = useCallback((inst: Instrument) => {
    const resolved = resolveTradingViewSymbol(inst.tradingview_symbol);
    if (resolved && inst.supported) {
      setTvSymbol(resolved);
      setCurrentInstrument(inst);
      setInstrumentPopoverOpen(false);
      navigate(`/trading-zone?symbol=${encodeURIComponent(resolved)}`, { replace: true });
    } else {
      setShowUnsupportedModal(true);
    }
  }, [navigate]);

  const handleUnsupportedClose = () => {
    setShowUnsupportedModal(false);
    navigate('/', { state: { defaultTab: 'market' }, replace: true });
  };

  const handleIndicatorToggle = useCallback((id: string) => {
    setActiveIndicators(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  }, []);

  const studies = useMemo(
    () => activeIndicators.map(id => INDICATOR_STUDY_MAP[id]).filter(Boolean),
    [activeIndicators]
  );

  const cleanSymbol = currentInstrument?.internal_symbol || '';
  const forexPair = cleanSymbol.includes('/') ? cleanSymbol : `${cleanSymbol.slice(0, 3)}/${cleanSymbol.slice(3)}`;
  const currentTick = ticks.get(forexPair) || ticks.get(cleanSymbol);
  const displayPrice = isCommodity ? marketPrice : currentTick?.price ?? null;
  const currentMp = currentInstrument
    ? prices.find(
        p =>
          p.symbol === currentInstrument.internal_symbol ||
          p.symbol === currentInstrument.display_name ||
          p.symbol === `${currentInstrument.internal_symbol.slice(0, 3)}/${currentInstrument.internal_symbol.slice(3)}`
      )
    : null;
  const changePercent = currentMp?.change_percent ?? 0;

  // BID/ASK/SPREAD
  const spread = getSpread(currentInstrument);
  const midPrice = displayPrice ?? currentMp?.price ?? 0;
  const bid = midPrice > 0 ? midPrice - spread / 2 : 0;
  const ask = midPrice > 0 ? midPrice + spread / 2 : 0;
  const decimals = currentInstrument?.decimals ?? 2;
  const spreadDisplay = currentInstrument?.asset_class === 'forex'
    ? (spread * (decimals === 2 ? 100 : 10000)).toFixed(1)
    : spread.toFixed(2);

  // Approximate H/L
  const approxHigh = midPrice > 0 ? midPrice * 1.003 : 0;
  const approxLow = midPrice > 0 ? midPrice * 0.997 : 0;

  // Group instruments for dropdown
  const groupedInstruments = useMemo(() => {
    const groups: Record<string, Instrument[]> = {};
    instrumentRegistry.filter(i => i.supported).forEach(inst => {
      const key = inst.category || inst.asset_class;
      if (!groups[key]) groups[key] = [];
      groups[key].push(inst);
    });
    return groups;
  }, []);

  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      <UnsupportedInstrumentModal
        open={showUnsupportedModal}
        onClose={handleUnsupportedClose}
        symbol={symbolParam}
      />

      {/* ─── PROFESSIONAL HEADER BAR ─── */}
      <div
        className="flex items-center justify-between px-3 shrink-0 bg-[hsl(var(--background-secondary))] border-b border-[hsl(var(--card-border))]"
        style={{ height: 48 }}
      >
        {/* LEFT: Back + Instrument Dropdown */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/', { state: { defaultTab: 'market' } })}
            className="p-1.5 rounded hover:bg-accent transition-colors"
          >
            <ArrowLeft className="w-4 h-4 text-muted-foreground" />
          </button>

          <div className="w-px h-5 bg-[hsl(var(--card-border))]" />

          <Popover open={instrumentPopoverOpen} onOpenChange={setInstrumentPopoverOpen}>
            <PopoverTrigger asChild>
              <button className="flex items-center gap-1.5 px-2 py-1 rounded hover:bg-accent transition-colors">
                <span className="text-sm font-semibold text-foreground">
                  {currentInstrument?.display_name || tvSymbol}
                </span>
                <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              sideOffset={4}
              className="p-0 w-64 bg-[hsl(var(--background-secondary))] border-[hsl(var(--card-border))]"
            >
              <ScrollArea className="h-80">
                {Object.entries(groupedInstruments).map(([group, instruments]) => (
                  <div key={group}>
                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                      {group}
                    </div>
                    {instruments.map(inst => {
                      const mp = prices.find(p => p.symbol === inst.internal_symbol || p.symbol === inst.display_name);
                      const isActive = inst.tradingview_symbol === tvSymbol;
                      return (
                        <button
                          key={inst.internal_symbol}
                          onClick={() => handleSymbolChange(inst)}
                          className={`w-full flex items-center justify-between px-3 py-1.5 text-xs transition-colors ${
                            isActive ? 'bg-[hsl(var(--bull)/0.1)] text-foreground' : 'text-muted-foreground hover:bg-accent'
                          }`}
                        >
                          <span className="font-medium">{inst.display_name}</span>
                          <span className="font-mono-data text-muted-foreground">
                            {mp ? mp.price.toFixed(inst.decimals) : '--'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </ScrollArea>
            </PopoverContent>
          </Popover>
        </div>

        {/* CENTER: BID / ASK / SPREAD — hidden on mobile */}
        <div className="hidden md:flex flex-col items-center">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] uppercase text-muted-foreground font-medium">BID</span>
              <span className="text-sm font-mono-data font-bold text-[hsl(var(--bull))]">{midPrice > 0 ? bid.toFixed(decimals) : '--'}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] uppercase text-muted-foreground font-medium">ASK</span>
              <span className="text-sm font-mono-data font-bold text-[hsl(var(--bear))]">{midPrice > 0 ? ask.toFixed(decimals) : '--'}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] uppercase text-muted-foreground font-medium">SPREAD</span>
              <span className="text-xs font-mono-data text-muted-foreground">{midPrice > 0 ? spreadDisplay : '--'}</span>
            </div>
            {changePercent !== 0 && (
              <span className={`text-xs font-mono-data font-medium ${changePercent > 0 ? 'text-[hsl(var(--bull))]' : 'text-[hsl(var(--bear))]'}`}>
                {changePercent > 0 ? '+' : ''}{changePercent.toFixed(2)}%
              </span>
            )}
          </div>
          {midPrice > 0 && (
            <div className="flex items-center gap-3 text-[9px] font-mono-data text-muted-foreground">
              <span>H: {approxHigh.toFixed(decimals)}</span>
              <span>L: {approxLow.toFixed(decimals)}</span>
            </div>
          )}
        </div>

        {/* Mobile: compact price */}
        <div className="md:hidden flex items-center gap-2">
          <span className="text-sm font-mono-data font-bold text-foreground">{midPrice > 0 ? midPrice.toFixed(decimals) : '--'}</span>
          {changePercent !== 0 && (
            <span className={`text-[10px] font-mono-data ${changePercent > 0 ? 'text-[hsl(var(--bull))]' : 'text-[hsl(var(--bear))]'}`}>
              {changePercent > 0 ? '+' : ''}{changePercent.toFixed(2)}%
            </span>
          )}
        </div>

        {/* RIGHT: Status + Actions — hidden on mobile */}
        <div className="hidden md:flex items-center gap-3">
          <BrokerSwitcherPill brokerName={brokerName} />
          {tradingState === 'broker-connected' && (
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--bull))] animate-pulse" />
              <span className="text-[10px] text-[hsl(var(--bull))] font-medium tracking-wide">LIVE</span>
            </div>
          )}
          {tradingState === 'demo-only' && (
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-accent-amber" />
              <span className="text-[10px] text-accent-amber font-medium tracking-wide">DEMO</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <ShieldCheck className="w-3 h-3 text-[hsl(var(--bull)/0.7)]" />
            <span className="text-[10px] text-[hsl(var(--bull)/0.7)] font-medium">Verified</span>
          </div>
          <button className="p-1.5 rounded hover:bg-accent transition-colors">
            <Bell className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
          <button className="p-1.5 rounded hover:bg-accent transition-colors">
            <Settings className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        </div>
      </div>

      {/* ─── AI Signal Banner ─── */}
      {aiSignal && (
        <div className="shrink-0 flex items-center gap-2 px-4 py-2 bg-[hsl(var(--accent-purple)/0.1)] border-b border-[hsl(var(--accent-purple)/0.2)]">
          <Brain className="w-4 h-4 text-[hsl(var(--accent-purple))]" />
          <span className="text-xs text-foreground">
            AI suggests{' '}
            <span className={`font-semibold ${aiSignal.side === 'buy' ? 'text-[hsl(var(--bull))]' : 'text-[hsl(var(--bear))]'}`}>
              {aiSignal.side.toUpperCase()}
            </span>{' '}
            with{' '}
            <span className="font-mono-data font-semibold text-[hsl(var(--accent-purple))]">
              {Math.round(aiSignal.confidence * 100)}%
            </span>{' '}
            confidence
          </span>
          <span className="text-[10px] text-muted-foreground ml-auto">Pending review</span>
        </div>
      )}

      {/* ─── Demo-only broker banner ─── */}
      {tradingState === 'demo-only' && (
        <div className="shrink-0 flex items-center justify-between px-4 py-2 bg-accent-amber/10 border-b border-accent-amber/20">
          <span className="text-xs text-foreground">
            You're in <span className="font-semibold text-accent-amber">Demo Mode</span>. Connect a broker for live AI-powered trading.
          </span>
          <button
            onClick={() => navigate('/brokers')}
            className="flex items-center gap-1 text-xs font-medium text-accent-amber hover:underline"
          >
            Connect Broker <ExternalLink className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* ─── MAIN GRID — 3-col desktop, stacked mobile ─── */}
      <div className="flex-1 overflow-hidden grid" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
        {/* Desktop: 3 columns */}
        <div className="hidden md:grid flex-1 overflow-hidden" style={{ gridTemplateColumns: '220px 1fr 280px' }}>
          <InstrumentList activeSymbol={tvSymbol} onSelect={handleSymbolChange} connectedBroker={brokerName} />
          <div className="flex flex-col overflow-hidden border-r border-[hsl(var(--card-border))]">
            <ChartToolbar
              interval={interval}
              onIntervalChange={setInterval}
              onIndicatorToggle={handleIndicatorToggle}
              activeIndicators={activeIndicators}
              symbol={currentInstrument?.display_name || ''}
            />
            <div className="flex-1 min-h-0">
              {tvSymbol && (
                <MarketChart symbol={tvSymbol} interval={interval} theme="dark" className="h-full" studies={studies} />
              )}
            </div>
          </div>
          <OrderPanel
            symbol={cleanSymbol}
            currentPrice={displayPrice ?? 0}
            instrumentDecimals={currentInstrument?.decimals ?? 2}
            tradingState={tradingState}
            brokerName={brokerName}
            demoAccountId={demoAccountId}
          />
        </div>

        {/* Mobile: tabbed Chart / Order view */}
        <MobileTradingTabs
          tvSymbol={tvSymbol}
          interval={interval}
          setInterval={setInterval}
          handleIndicatorToggle={handleIndicatorToggle}
          activeIndicators={activeIndicators}
          currentInstrument={currentInstrument}
          studies={studies}
          cleanSymbol={cleanSymbol}
          displayPrice={displayPrice}
          tradingState={tradingState}
          brokerName={brokerName}
          demoAccountId={demoAccountId}
        />
      </div>
    </div>
  );
};

export default TradingZone;
